import React, { useState } from 'react';
import { MapPin, Clock, Calendar, ChevronDown, ChevronUp, Map } from 'lucide-react';
import RouteMap from '../RouteMap';

const TripSummaryCard = ({ tripDetails }) => {
  // Mobile map collapsed by default
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  
  const {
    pickup = 'Istanbul Airport (IST)',
    dropoff = 'Taksim Square',
    date = '',
    time = '',
    formattedDate = '',  // Pre-formatted from session
    timezone = 'Europe/Istanbul',
    distance = '45',
    durationMinutes = '55',
  } = tripDetails;

  // Format date from raw strings - no timezone conversion
  const formatDateFromParts = (dateStr, timeStr) => {
    if (!dateStr) return 'Date not set';
    
    try {
      // Parse YYYY-MM-DD
      const [year, month, day] = dateStr.split('-').map(Number);
      
      // Create date just to get weekday/month names
      const d = new Date(year, month - 1, day);
      const weekday = d.toLocaleDateString('en-GB', { weekday: 'short' });
      const monthName = d.toLocaleDateString('en-GB', { month: 'short' });
      
      return `${weekday}, ${day} ${monthName} ${year} at ${timeStr || '00:00'}`;
    } catch (e) {
      return `${dateStr} ${timeStr || ''}`;
    }
  };

  // Use pre-formatted date if available, otherwise format from parts
  const displayDate = formattedDate || formatDateFromParts(date, time);

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm" data-testid="trip-summary-card">
      {/* Map Preview - Desktop always visible, Mobile collapsible */}
      <div className="hidden md:block relative h-48 bg-gray-900">
        <RouteMap
          pickup={pickup}
          dropoff={dropoff}
          distance={distance}
          durationMinutes={durationMinutes}
          height="192px"
          showInfo={true}
        />
      </div>
      
      {/* Mobile Map Toggle */}
      <div className="md:hidden">
        <button
          onClick={() => setIsMapExpanded(!isMapExpanded)}
          className="w-full flex items-center justify-between p-3 bg-gray-50 border-b border-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          data-testid="mobile-map-toggle"
        >
          <span className="flex items-center gap-2">
            <Map className="h-4 w-4 text-[#FF8C00]" />
            {isMapExpanded ? 'Hide map' : 'Show map'}
          </span>
          {isMapExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        
        {/* Collapsible Map */}
        {isMapExpanded && (
          <div className="relative h-48 bg-gray-900 animate-in slide-in-from-top duration-200">
            <RouteMap
              pickup={pickup}
              dropoff={dropoff}
              distance={distance}
              durationMinutes={durationMinutes}
              height="192px"
              showInfo={true}
            />
          </div>
        )}
      </div>

      {/* Trip Details */}
      <div className="p-4 space-y-4">
        <h3 className="font-semibold text-black text-sm uppercase tracking-wider">
          Trip Summary
        </h3>

        {/* Pickup */}
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-[#FF8C00]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <div className="w-2 h-2 rounded-full bg-[#FF8C00]" />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Pickup</p>
            <p className="text-sm font-medium text-black">{pickup}</p>
          </div>
        </div>

        {/* Connector */}
        <div className="ml-3 w-0.5 h-4 bg-gray-200 border-l-2 border-dashed border-gray-300" />

        {/* Dropoff */}
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-[#FF8C00] flex items-center justify-center flex-shrink-0 mt-0.5">
            <MapPin className="h-3 w-3 text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Drop-off</p>
            <p className="text-sm font-medium text-black">{dropoff}</p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 pt-4">
          {/* Date & Time */}
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Date & Time</p>
              <p className="text-sm font-medium text-black" data-testid="trip-date-display">
                {displayDate}
              </p>
              <p className="text-xs text-gray-400">Turkey Time (GMT+3)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripSummaryCard;
